import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import CursorContext from '../../providers/Cursor';

const variants = {
    // Crema sobre fondo oscuro: el cursor pasa a punto oscuro para verse encima
    solid: { className: 'bg-cream text-ink hover:bg-white focus-visible:outline-cream', arrow: 'bg-ink text-cream', cursor: 'select' },
    // Vidrio oscuro sobre video o spotlight: se lee igual sobre negro y sobre crema
    ghost: { className: 'bg-ink/60 text-cream ring-1 ring-inset ring-cream/25 backdrop-blur-md hover:bg-ink/80 hover:ring-cream/60 focus-visible:outline-cream', arrow: 'bg-cream text-ink', cursor: 'dark' },
    // Negro sobre fondo crema
    dark: { className: 'bg-ink text-cream hover:bg-secondary focus-visible:outline-ink', arrow: 'bg-cream text-ink', cursor: 'dark' },
};

/**
 * Botón píldora con flecha que se desliza en hover. Usa NavLink para rutas
 * internas (`to`), <a> para externas o anclas (`href`) y <button> si no
 * recibe ninguna de las dos (p. ej. `type="submit"` en un formulario).
 * Con `soloVisual` es un <span>: para tarjetas que ya son un link, donde otro
 * elemento interactivo adentro no es válido. La flecha se mueve con el hover
 * de la tarjeta.
 */
export default function PillLink({ to, href, children, variant = 'solid', className = '', external = false, soloVisual = false, ...rest }) {
    const { selectEnter, blackSectionEnter } = useContext(CursorContext);
    const v = variants[variant] || variants.solid;

    const content = (
        <>
            <span>{children}</span>
            <span className={`relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full ${v.arrow}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 transition-transform duration-500 ease-expo group-hover:translate-x-8" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute h-4 w-4 -translate-x-8 transition-transform duration-500 ease-expo group-hover:translate-x-0" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
            </span>
        </>
    );

    const props = {
        onMouseEnter: v.cursor === 'select' ? selectEnter : blackSectionEnter,
        className: `group inline-flex h-12 items-center gap-3 rounded-full pl-6 pr-2 text-base font-medium transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 disabled:cursor-wait disabled:opacity-60 ${v.className} ${className}`,
        ...rest,
    };

    if (soloVisual) return <span aria-hidden="true" className={props.className}>{content}</span>;
    if (to) return <NavLink to={to} {...props}>{content}</NavLink>;
    if (!href) return <button type="button" {...props}>{content}</button>;
    return (
        <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...props}>
            {content}
        </a>
    );
}
